import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faEnvelope,
  faServer,
  faLock,
  faEye,
  faEyeSlash,
  faSave,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faPaperPlane,
  faShieldAlt,
  faKey,
  faSync,
  faCloud
} from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../services/api';
import './AdminSettings.css';

interface SMTPSettings {
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_use_tls: boolean;
  smtp_use_ssl: boolean;
  from_email: string;
  from_name: string;
}

interface SystemSettings {
  smtp: SMTPSettings;
  registration_enabled: boolean;
  require_admin_approval: boolean;
  password_min_length: number;
  session_timeout_hours: number;
  domain: string;
}

interface AIConsiliumStatus {
  status: 'connected' | 'error' | 'unknown';
  base_url: string;
  knowledge_bucket: string;
  orchestrator: string;
  workspace_id: string;
  message?: string;
}

export const AdminSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    smtp: {
      smtp_host: '',
      smtp_port: 587,
      smtp_username: '',
      smtp_password: '',
      smtp_use_tls: true,
      smtp_use_ssl: false,
      from_email: '',
      from_name: 'System Radonek'
    },
    registration_enabled: true,
    require_admin_approval: false,
    password_min_length: 8,
    session_timeout_hours: 24,
    domain: 'localhost:8080'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingPDF, setTestingPDF] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saveStatus, setSaveStatus] = useState<'success' | 'error' | null>(null);
  const [testStatus, setTestStatus] = useState<'success' | 'error' | null>(null);
  const [aiStatus, setAiStatus] = useState<AIConsiliumStatus | null>(null);
  const [loadingAiStatus, setLoadingAiStatus] = useState(false);

  useEffect(() => {
    loadSettings();
    loadAIStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getSystemSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAIStatus = async () => {
    setLoadingAiStatus(true);
    try {
      const response = await fetch('/api/admin/ai-consilium-status', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAiStatus(data);
      } else {
        setAiStatus({
          status: 'error',
          base_url: 'Unknown',
          knowledge_bucket: 'Unknown',
          orchestrator: 'Unknown',
          workspace_id: 'Unknown',
          message: 'Nie udało się pobrać statusu'
        });
      }
    } catch (error) {
      console.error('Error loading AI status:', error);
      setAiStatus({
        status: 'error',
        base_url: 'Unknown',
        knowledge_bucket: 'Unknown',
        orchestrator: 'Unknown',
        workspace_id: 'Unknown',
        message: 'Błąd połączenia'
      });
    } finally {
      setLoadingAiStatus(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveStatus(null);

    try {
      await adminAPI.updateSystemSettings(settings);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    if (!testEmail) {
      alert('Proszę podać adres email do testu');
      return;
    }

    setTesting(true);
    setTestStatus(null);

    try {
      await adminAPI.testSMTPConnection({
        ...settings.smtp,
        test_email: testEmail
      });
      setTestStatus('success');
      setTimeout(() => setTestStatus(null), 3000);
    } catch (error) {
      console.error('Error testing SMTP:', error);
      setTestStatus('error');
      setTimeout(() => setTestStatus(null), 3000);
    } finally {
      setTesting(false);
    }
  };

  const handleTestInvitationPDF = async () => {
    setTestingPDF(true);
    try {
      const blob = await adminAPI.generateTestInvitationPDF();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      alert('Testowy PDF zaproszenia został wygenerowany i otwarty w nowej karcie!');
    } catch (error) {
      console.error('Error generating test invitation PDF:', error);
      alert('Błąd podczas generowania testowego PDF zaproszenia.');
    } finally {
      setTestingPDF(false);
    }
  };

  const handleTestConsentPDF = async () => {
    setTestingPDF(true);
    try {
      const blob = await adminAPI.generateTestConsentPDF();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      alert('Testowy PDF zgody został wygenerowany i otwarty w nowej karcie!');
    } catch (error) {
      console.error('Error generating test consent PDF:', error);
      alert('Błąd podczas generowania testowego PDF zgody.');
    } finally {
      setTestingPDF(false);
    }
  };

  const updateSMTPSetting = (key: keyof SMTPSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      smtp: {
        ...prev.smtp,
        [key]: value
      }
    }));
  };

  const updateSystemSetting = (key: keyof SystemSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (loading) {
    return (
      <div className="admin-settings">
        <div className="settings-header">
          <h1><FontAwesomeIcon icon={faCog} /> Ustawienia systemu</h1>
        </div>
        <div className="loading">Ładowanie ustawień...</div>
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <div className="settings-header">
        <h1><FontAwesomeIcon icon={faCog} /> Ustawienia systemu</h1>
        <p>Konfiguracja serwera SMTP, usługi AI i ustawień rejestracji</p>
        <div className="header-actions">
          <button
            onClick={handleSaveSettings}
            className="save-button"
            disabled={saving}
          >
            <FontAwesomeIcon icon={faSave} className="button-icon" />
            {saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}
          </button>
          {saveStatus && (
            <div className={`status-message ${saveStatus}`}>
              <FontAwesomeIcon icon={saveStatus === 'success' ? faCheck : faExclamationTriangle} />
              {saveStatus === 'success' ? 'Ustawienia zapisane' : 'Błąd podczas zapisywania'}
            </div>
          )}
        </div>
      </div>

      <div className="settings-content">
        {/* AI Consilium Status */}
        <div className="settings-section card">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faCloud} /> Usługa AI Consilium</h2>
            <p>Status połączenia z centralną usługą AI</p>
          </div>

          <div className="settings-grid">
            {loadingAiStatus ? (
              <div className="setting-group full-width">
                <p><FontAwesomeIcon icon={faSync} className="spinning" /> Ładowanie statusu...</p>
              </div>
            ) : aiStatus ? (
              <>
                <div className="setting-group full-width">
                  <div className={`status-display ${aiStatus.status === 'connected' ? 'healthy' : 'error'}`}>
                    <h4>
                      <FontAwesomeIcon icon={faServer} />
                      Status: {aiStatus.status === 'connected' ? 'Połączono' : 'Błąd połączenia'}
                    </h4>
                    {aiStatus.message && <p className="error-message">{aiStatus.message}</p>}
                  </div>
                </div>

                <div className="setting-group">
                  <label>URL usługi</label>
                  <div className="readonly-value">{aiStatus.base_url}</div>
                </div>

                <div className="setting-group">
                  <label>Baza wiedzy</label>
                  <div className="readonly-value">{aiStatus.knowledge_bucket}</div>
                </div>

                <div className="setting-group">
                  <label>Orkiestrator</label>
                  <div className="readonly-value">{aiStatus.orchestrator}</div>
                </div>

                <div className="setting-group">
                  <label>Workspace ID</label>
                  <div className="readonly-value">{aiStatus.workspace_id}</div>
                </div>
              </>
            ) : (
              <div className="setting-group full-width">
                <p>Nie udało się pobrać statusu usługi AI.</p>
              </div>
            )}

            <div className="setting-group full-width">
              <button
                type="button"
                onClick={loadAIStatus}
                disabled={loadingAiStatus}
                className="test-button"
              >
                <FontAwesomeIcon icon={faSync} className={loadingAiStatus ? 'spinning' : ''} />
                {loadingAiStatus ? 'Sprawdzanie...' : 'Odśwież status'}
              </button>
            </div>
          </div>

          <div className="info-box">
            <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            <div className="info-content">
              <strong>AI Consilium</strong>
              <p>
                System RADONEK korzysta z centralnej usługi AI Consilium do obsługi zapytań pacjentów.
                Usługa zapewnia:
              </p>
              <ul>
                <li><strong>Bazę wiedzy "notatnik":</strong> Dostęp do medycznych wytycznych i materiałów edukacyjnych</li>
                <li><strong>Model AI:</strong> Zaawansowany model językowy zoptymalizowany dla kontekstu medycznego</li>
                <li><strong>Pamięć konwersacji:</strong> Kontynuacja rozmów z pacjentami</li>
                <li><strong>Streaming odpowiedzi:</strong> Natychmiastowe wyświetlanie generowanych odpowiedzi</li>
              </ul>
              <p>
                <strong>Uwaga:</strong> Konfiguracja usługi AI jest zarządzana centralnie i nie wymaga zmian w tym panelu.
              </p>
            </div>
          </div>
        </div>

        {/* SMTP Configuration */}
        <div className="settings-section card">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faEnvelope} /> Konfiguracja SMTP</h2>
            <p>Ustawienia serwera pocztowego do wysyłania emaili systemowych</p>
          </div>

          <div className="settings-grid">
            <div className="setting-group">
              <label htmlFor="smtp_host">
                <FontAwesomeIcon icon={faServer} /> Serwer SMTP
              </label>
              <input
                id="smtp_host"
                type="text"
                value={settings.smtp.smtp_host}
                onChange={e => updateSMTPSetting('smtp_host', e.target.value)}
                placeholder="np. smtp.gmail.com"
              />
            </div>

            <div className="setting-group">
              <label htmlFor="smtp_port">Port</label>
              <input
                id="smtp_port"
                type="number"
                value={settings.smtp.smtp_port}
                onChange={e => updateSMTPSetting('smtp_port', parseInt(e.target.value) || 587)}
                placeholder="587"
              />
            </div>

            <div className="setting-group">
              <label htmlFor="smtp_username">
                <FontAwesomeIcon icon={faEnvelope} /> Nazwa użytkownika
              </label>
              <input
                id="smtp_username"
                type="text"
                value={settings.smtp.smtp_username}
                onChange={e => updateSMTPSetting('smtp_username', e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            <div className="setting-group">
              <label htmlFor="smtp_password">
                <FontAwesomeIcon icon={faLock} /> Hasło
              </label>
              <div className="password-input-group">
                <input
                  id="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={settings.smtp.smtp_password}
                  onChange={e => updateSMTPSetting('smtp_password', e.target.value)}
                  placeholder="Hasło do konta email"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="setting-group">
              <label htmlFor="from_email">Email nadawcy</label>
              <input
                id="from_email"
                type="email"
                value={settings.smtp.from_email}
                onChange={e => updateSMTPSetting('from_email', e.target.value)}
                placeholder="noreply@example.com"
              />
            </div>

            <div className="setting-group">
              <label htmlFor="from_name">Nazwa nadawcy</label>
              <input
                id="from_name"
                type="text"
                value={settings.smtp.from_name}
                onChange={e => updateSMTPSetting('from_name', e.target.value)}
                placeholder="System Radonek"
              />
            </div>
          </div>

          <div className="security-options">
            <h3><FontAwesomeIcon icon={faShieldAlt} /> Opcje bezpieczeństwa</h3>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.smtp.smtp_use_tls}
                  onChange={e => updateSMTPSetting('smtp_use_tls', e.target.checked)}
                />
                <span className="checkmark"></span>
                Użyj TLS (rekomendowane dla portu 587)
              </label>
            </div>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.smtp.smtp_use_ssl}
                  onChange={e => updateSMTPSetting('smtp_use_ssl', e.target.checked)}
                />
                <span className="checkmark"></span>
                Użyj SSL (rekomendowane dla portu 465)
              </label>
            </div>
          </div>

          <div className="smtp-test">
            <h3><FontAwesomeIcon icon={faPaperPlane} /> Test połączenia SMTP</h3>
            <div className="test-input-group">
              <input
                type="email"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                placeholder="Adres email do testu"
                className="test-email-input"
              />
              <button
                onClick={handleTestSMTP}
                className="test-button"
                disabled={testing || !testEmail}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="button-icon" />
                {testing ? 'Testowanie...' : 'Wyślij test'}
              </button>
            </div>
            {testStatus && (
              <div className={`status-message ${testStatus}`}>
                <FontAwesomeIcon icon={testStatus === 'success' ? faCheck : faExclamationTriangle} />
                {testStatus === 'success' ? 'Email testowy wysłany pomyślnie' : 'Błąd podczas wysyłania emaila'}
              </div>
            )}
          </div>
        </div>

        {/* Registration Settings */}
        <div className="settings-section card">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faKey} /> Ustawienia rejestracji</h2>
            <p>Konfiguracja procesu rejestracji nowych użytkowników</p>
          </div>

          <div className="settings-grid">
            <div className="checkbox-group full-width">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.registration_enabled}
                  onChange={e => updateSystemSetting('registration_enabled', e.target.checked)}
                />
                <span className="checkmark"></span>
                Włącz rejestrację nowych użytkowników
              </label>
            </div>

            <div className="checkbox-group full-width">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.require_admin_approval}
                  onChange={e => updateSystemSetting('require_admin_approval', e.target.checked)}
                />
                <span className="checkmark"></span>
                Wymagaj zatwierdzenia przez administratora
              </label>
            </div>

            <div className="setting-group">
              <label htmlFor="password_min_length">
                <FontAwesomeIcon icon={faLock} /> Minimalna długość hasła
              </label>
              <input
                id="password_min_length"
                type="number"
                min="6"
                max="32"
                value={settings.password_min_length}
                onChange={e => updateSystemSetting('password_min_length', parseInt(e.target.value) || 8)}
              />
            </div>

            <div className="setting-group">
              <label htmlFor="session_timeout">Czas sesji (godziny)</label>
              <input
                id="session_timeout"
                type="number"
                min="1"
                max="168"
                value={settings.session_timeout_hours}
                onChange={e => updateSystemSetting('session_timeout_hours', parseInt(e.target.value) || 24)}
              />
            </div>

            <div className="setting-group">
              <label htmlFor="domain">Domena systemu</label>
              <input
                id="domain"
                type="text"
                placeholder="np. radonek.example.com lub localhost:8080"
                value={settings.domain}
                onChange={e => updateSystemSetting('domain', e.target.value)}
              />
              <small className="form-help">
                Używana w linkach aktywacyjnych i resetowania hasła w emailach
              </small>
            </div>
          </div>

          <div className="info-box">
            <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            <div className="info-content">
              <strong>Informacje o ustawieniach:</strong>
              <ul>
                <li>Po włączeniu rejestracji, administrator może tworzyć konta, które wyślą email aktywacyjny</li>
                <li>Zatwierdzenie przez administratora wymaga manualnej aktywacji każdego konta</li>
                <li>Minimalna długość hasła obowiązuje przy ustawianiu i zmianie haseł</li>
                <li>Czas sesji określa, po jakim czasie użytkownik zostanie automatycznie wylogowany</li>
                <li>Domena systemu jest używana w linkach aktywacyjnych i resetowania hasła wysyłanych w emailach</li>
              </ul>
            </div>
          </div>
        </div>

        {/* PDF Testing */}
        <div className="settings-section card">
          <div className="section-header">
            <h2><FontAwesomeIcon icon={faPaperPlane} /> Testowanie PDF</h2>
            <p>Generowanie testowych plików PDF dla sprawdzenia formatowania</p>
          </div>

          <div className="settings-grid">
            <div className="setting-group">
              <h4>PDF zaproszenia</h4>
              <p>Wygeneruj testowy PDF z zaproszeniem dla pacjenta z przykładowymi danymi.</p>
              <button
                type="button"
                onClick={handleTestInvitationPDF}
                disabled={testingPDF}
                className="test-button"
              >
                <FontAwesomeIcon icon={testingPDF ? faSync : faPaperPlane} className={testingPDF ? 'spinning' : ''} />
                {testingPDF ? 'Generowanie...' : 'Generuj test zaproszenia PDF'}
              </button>
            </div>

            <div className="setting-group">
              <h4>PDF zgody</h4>
              <p>Wygeneruj testowy PDF z formularzem zgody z przykładowymi danymi.</p>
              <button
                type="button"
                onClick={handleTestConsentPDF}
                disabled={testingPDF}
                className="test-button"
              >
                <FontAwesomeIcon icon={testingPDF ? faSync : faPaperPlane} className={testingPDF ? 'spinning' : ''} />
                {testingPDF ? 'Generowanie...' : 'Generuj test zgody PDF'}
              </button>
            </div>
          </div>

          <div className="info-box">
            <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            <div className="info-content">
              <strong>Informacje o testach PDF:</strong>
              <ul>
                <li>Testowe pliki PDF używają przykładowych danych pacjentów</li>
                <li>Pozwalają sprawdzić formatowanie i czytelność polskich znaków</li>
                <li>Pliki są automatycznie otwierane w nowej karcie przeglądarki</li>
                <li>Przydatne do testowania zmian w układzie lub czcionkach</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
